/**
 * Legal Pages Tab
 *
 * Admin section to edit legal pages content (Privacy, Terms, About, Contact).
 * Features a Markdown editor with live preview.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Button } from '../../components/ui';
import { legalPagesService } from '../../services/legal-pages.service';
import type { LegalPage, LegalPageId } from '@budgira/shared';
import { LEGAL_PAGES_META } from '@budgira/shared';
import ReactMarkdown from 'react-markdown';

type EditorMode = 'edit' | 'preview' | 'split';

const LegalPagesTab = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language.startsWith('en') ? 'en' : 'fr';

  // State
  const [pages, setPages] = useState<LegalPage[]>([]);
  const [selectedPageId, setSelectedPageId] = useState<LegalPageId>('about');
  const [content, setContent] = useState('');
  const [originalContent, setOriginalContent] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [editorMode, setEditorMode] = useState<EditorMode>('split');

  // Load pages on mount
  useEffect(() => {
    loadPages();
  }, []);

  // Clear success message after 5 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const loadPages = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await legalPagesService.getAllLegalPages();
      setPages(data);

      // Select first page and load its content
      if (data.length > 0) {
        const firstPage = data.find((p) => p.pageId === selectedPageId) || data[0];
        if (firstPage) {
          setSelectedPageId(firstPage.pageId);
          setContent(firstPage.content);
          setOriginalContent(firstPage.content);
        }
      }
    } catch (err) {
      console.error('Failed to load legal pages:', err);
      setError(t('admin.legalPages.errors.loadFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageSelect = (pageId: LegalPageId) => {
    // Check for unsaved changes
    if (content !== originalContent) {
      if (!confirm(t('admin.legalPages.unsavedWarning'))) {
        return;
      }
    }

    setSelectedPageId(pageId);
    const page = pages.find((p) => p.pageId === pageId);
    if (page) {
      setContent(page.content);
      setOriginalContent(page.content);
    }
    setError(null);
    setSuccess(false);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const updatedPage = await legalPagesService.updateLegalPage(selectedPageId, content);

      // Update local state
      setPages((prev) =>
        prev.map((p) => (p.pageId === selectedPageId ? updatedPage : p))
      );
      setOriginalContent(content);
      setSuccess(true);
    } catch (err) {
      console.error('Failed to save legal page:', err);
      setError(
        err instanceof Error ? err.message : t('admin.legalPages.errors.saveFailed')
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setContent(originalContent);
  };

  const hasChanges = content !== originalContent;

  const selectedPage = pages.find((p) => p.pageId === selectedPageId);
  const pageTitle =
    lang === 'fr'
      ? LEGAL_PAGES_META[selectedPageId]?.titleFr
      : LEGAL_PAGES_META[selectedPageId]?.titleEn;

  // Render loading state
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-slate-200 dark:bg-slate-700 rounded w-48 animate-pulse" />
        <div className="h-96 bg-slate-100 dark:bg-slate-800 rounded-xl animate-pulse" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
          {t('admin.legalPages.title')}
        </h2>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {t('admin.legalPages.description')}
        </p>
      </div>

      {/* Error message */}
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Success message */}
      {success && (
        <div className="p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-400">
          {t('admin.legalPages.saveSuccess')}
        </div>
      )}

      {/* Page selector tabs */}
      <div className="flex flex-wrap gap-2">
        {(Object.keys(LEGAL_PAGES_META) as LegalPageId[]).map((pageId) => {
          const meta = LEGAL_PAGES_META[pageId];
          const isSelected = pageId === selectedPageId;
          const title = lang === 'fr' ? meta.titleFr : meta.titleEn;

          return (
            <button
              key={pageId}
              onClick={() => handlePageSelect(pageId)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                isSelected
                  ? 'bg-violet-600 text-white'
                  : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
              }`}
            >
              <span className="mr-2">{meta.icon}</span>
              {title}
            </button>
          );
        })}
      </div>

      {/* Editor */}
      <Card>
        {/* Editor toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4 pb-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
              {t('admin.legalPages.editing')}: {pageTitle}
            </span>
            {hasChanges && (
              <span className="px-2 py-0.5 bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 text-xs rounded-full">
                {t('admin.legalPages.unsaved')}
              </span>
            )}
          </div>

          {/* View mode toggle */}
          <div className="flex rounded-lg bg-slate-100 dark:bg-slate-800 p-1">
            <button
              onClick={() => setEditorMode('edit')}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                editorMode === 'edit'
                  ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-sm'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100'
              }`}
            >
              {t('admin.legalPages.modes.edit')}
            </button>
            <button
              onClick={() => setEditorMode('split')}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                editorMode === 'split'
                  ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-sm'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100'
              }`}
            >
              {t('admin.legalPages.modes.split')}
            </button>
            <button
              onClick={() => setEditorMode('preview')}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                editorMode === 'preview'
                  ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-sm'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100'
              }`}
            >
              {t('admin.legalPages.modes.preview')}
            </button>
          </div>
        </div>

        {/* Editor and preview panels */}
        <div
          className={`grid gap-4 ${
            editorMode === 'split' ? 'md:grid-cols-2' : 'grid-cols-1'
          }`}
        >
          {/* Editor panel */}
          {(editorMode === 'edit' || editorMode === 'split') && (
            <div className="space-y-2">
              {editorMode === 'split' && (
                <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  Markdown
                </h3>
              )}
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                className="w-full h-96 px-4 py-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 font-mono text-sm resize-none focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                placeholder={t('admin.legalPages.placeholder')}
                disabled={isSaving}
              />
            </div>
          )}

          {/* Preview panel */}
          {(editorMode === 'preview' || editorMode === 'split') && (
            <div className="space-y-2">
              {editorMode === 'split' && (
                <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  {t('admin.legalPages.preview')}
                </h3>
              )}
              <div className="h-96 px-4 py-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 overflow-auto">
                <article className="prose prose-slate dark:prose-invert prose-sm max-w-none">
                  <ReactMarkdown>{content}</ReactMarkdown>
                </article>
              </div>
            </div>
          )}
        </div>

        {/* Last updated info */}
        {selectedPage?.updatedAt && (
          <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">
            {t('admin.legalPages.lastUpdated')}:{' '}
            {new Date(selectedPage.updatedAt).toLocaleString()}
          </p>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-slate-200 dark:border-slate-700">
          <Button
            type="button"
            variant="secondary"
            onClick={handleReset}
            disabled={isSaving || !hasChanges}
          >
            {t('admin.legalPages.reset')}
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={isSaving || !hasChanges}
          >
            {isSaving ? t('common.saving') : t('common.save')}
          </Button>
        </div>
      </Card>

      {/* Markdown help */}
      <Card>
        <h3 className="text-sm font-medium text-slate-900 dark:text-slate-100 mb-3">
          {t('admin.legalPages.markdownHelp.title')}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div className="space-y-2 text-slate-600 dark:text-slate-400">
            <p>
              <code className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800 rounded">
                # Titre
              </code>{' '}
              - {t('admin.legalPages.markdownHelp.heading1')}
            </p>
            <p>
              <code className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800 rounded">
                ## Sous-titre
              </code>{' '}
              - {t('admin.legalPages.markdownHelp.heading2')}
            </p>
            <p>
              <code className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800 rounded">
                **gras**
              </code>{' '}
              - {t('admin.legalPages.markdownHelp.bold')}
            </p>
            <p>
              <code className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800 rounded">
                *italique*
              </code>{' '}
              - {t('admin.legalPages.markdownHelp.italic')}
            </p>
          </div>
          <div className="space-y-2 text-slate-600 dark:text-slate-400">
            <p>
              <code className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800 rounded">
                - item
              </code>{' '}
              - {t('admin.legalPages.markdownHelp.list')}
            </p>
            <p>
              <code className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800 rounded">
                1. item
              </code>{' '}
              - {t('admin.legalPages.markdownHelp.numberedList')}
            </p>
            <p>
              <code className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800 rounded">
                [texte](url)
              </code>{' '}
              - {t('admin.legalPages.markdownHelp.link')}
            </p>
            <p>
              <code className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800 rounded">
                ---
              </code>{' '}
              - {t('admin.legalPages.markdownHelp.separator')}
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default LegalPagesTab;
